import { WebContainer, type FileSystemTree } from '@webcontainer/api';

type LogFn = (line: string) => void;

const API_BASE_URL = (import.meta.env.VITE_API_URL as string) || 'http://localhost:3000/api';

// ---- dependency snapshot cache (IndexedDB) ---------------------------------
// Every HyVay app shares the scaffold's package.json, so the installed
// node_modules tree is identical across apps. The tree is obtained, in order of
// preference, from:
//   1. this browser's IndexedDB (a snapshot we mounted or exported before);
//   2. the platform (GET /studio/deps-snapshot/<sha256 of package.json>), a
//      snapshot exported ONCE from a WebContainer install and served as a
//      single immutable download;
//   3. `npm install` inside the container (~40-60s), after which the tree is
//      exported and cached in IndexedDB for the next boot.
// A mounted snapshot loses the executable bit on the `.bin` launchers (measured:
// `npm run dev` -> "jsh: spawn astro EACCES", exit 0), so every restore is
// followed by `chmod +x node_modules/.bin/*` before the dev server starts.

const SNAP_DB = 'hyvay-studio';
const SNAP_STORE = 'snapshots';
const SNAP_VERSION = 'v2'; // bump when the cache format or fix-up changes

async function sha256Hex(text: string): Promise<string> {
    const data = new TextEncoder().encode(text);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

function openSnapDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(SNAP_DB, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(SNAP_STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function snapGet(key: string): Promise<Uint8Array | null> {
    try {
        const db = await openSnapDb();
        return await new Promise((resolve) => {
            const req = db.transaction(SNAP_STORE).objectStore(SNAP_STORE).get(key);
            req.onsuccess = () => resolve(req.result instanceof Uint8Array ? req.result : null);
            req.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
}

async function snapDelete(key: string): Promise<void> {
    try {
        const db = await openSnapDb();
        await new Promise<void>((resolve) => {
            const tx = db.transaction(SNAP_STORE, 'readwrite');
            tx.objectStore(SNAP_STORE).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    } catch {
        /* best-effort */
    }
}

async function snapSet(key: string, value: Uint8Array): Promise<void> {
    try {
        const db = await openSnapDb();
        await new Promise<void>((resolve) => {
            const tx = db.transaction(SNAP_STORE, 'readwrite');
            tx.objectStore(SNAP_STORE).put(value, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    } catch {
        /* quota/private mode — cache is best-effort */
    }
}

/** Fetch the platform-hosted snapshot for this package.json hash; null when absent. */
async function fetchServerSnapshot(hash: string, onLog: LogFn): Promise<Uint8Array | null> {
    try {
        const t0 = performance.now();
        const res = await fetch(`${API_BASE_URL}/studio/deps-snapshot/${hash}`, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        const buf = new Uint8Array(await res.arrayBuffer());
        onLog(`Downloaded dependency snapshot (${(buf.byteLength / 1048576).toFixed(0)} MB) in ${((performance.now() - t0) / 1000).toFixed(1)}s\n`);
        return buf;
    } catch {
        return null;
    }
}

/** Flat path->contents map to the nested tree WebContainer.mount() wants. */
function toFileSystemTree(files: Record<string, string>): FileSystemTree {
    const root: FileSystemTree = {};
    for (const [p, contents] of Object.entries(files)) {
        const parts = p.split('/').filter(Boolean);
        let node = root;
        for (let i = 0; i < parts.length - 1; i++) {
            const seg = parts[i];
            const existing = node[seg];
            if (!existing || !('directory' in existing)) node[seg] = { directory: {} };
            node = (node[seg] as { directory: FileSystemTree }).directory;
        }
        node[parts[parts.length - 1]] = { file: { contents } };
    }
    return root;
}

type PendingOp =
    | { kind: 'write'; path: string; contents: string }
    | { kind: 'delete'; path: string }
    | { kind: 'rename'; from: string; to: string };

/**
 * Thin wrapper around a single WebContainer instance (only one is allowed per page).
 * Boots the container, mounts the app's workspace, gets node_modules in place
 * (snapshot or npm install), runs the dev server, and exposes writeFile for live
 * edits (Vite/Astro HMR patches the preview iframe).
 */
class WebContainerService {
    private instance: WebContainer | null = null;
    private booting: Promise<WebContainer> | null = null;
    private starting: Promise<string> | null = null;
    private previewUrl: string | null = null;
    private mounted = false;
    /** Which application's files are currently mounted (the container is a
     *  page singleton — switching apps REQUIRES a teardown + fresh mount). */
    private mountedFor: string | null = null;
    /** Set when a node_modules snapshot was mounted (skip npm install). */
    private depsFromCache = false;
    /** Cache key for the current project's dependency set. */
    private depsKey: string | null = null;
    /** Content hash of the mounted package.json (server snapshot lookup key). */
    private depsHash: string | null = null;
    /**
     * File operations requested BEFORE the workspace was mounted (the AI turn
     * now starts while the container is still booting). Replayed, in order,
     * right after mount — they are newer than the server snapshot we mount.
     */
    private pending: PendingOp[] = [];

    /** crossOriginIsolated is required; surface a clear error if headers are missing. */
    get isSupported(): boolean {
        return typeof window !== 'undefined' && (window as any).crossOriginIsolated === true;
    }

    /** Boot (once). Safe to call repeatedly. */
    async boot(): Promise<WebContainer> {
        if (this.instance) return this.instance;
        if (this.booting) return this.booting;

        if (!this.isSupported) {
            throw new Error(
                'WebContainers require cross-origin isolation (COOP/COEP headers). ' +
                'Ensure the dev server / route sends them and the page is crossOriginIsolated.',
            );
        }

        this.booting = (async () => {
            // credentialless (not the default require-corp): the preview documents
            // are then served with COEP: credentialless, so a plain <img> from a
            // host WITHOUT a Cross-Origin-Resource-Policy header still loads.
            // Under require-corp such images fail with
            // ERR_BLOCKED_BY_RESPONSE.NotSameOriginAfterDefaultedToSameOriginByCoep
            // (measured: picsum blocked, only CORP-sending hosts like Unsplash
            // loaded). The studio document itself already uses credentialless.
            const wc = await WebContainer.boot({ coep: 'credentialless' });
            this.instance = wc;
            return wc;
        })();

        return this.booting;
    }

    /** Whether THIS application's files are the ones currently mounted. */
    hasMountedFor(applicationId: string): boolean {
        return this.mounted && this.mountedFor === applicationId;
    }

    /**
     * Prepare the singleton for an application: if a DIFFERENT app is currently
     * mounted, tear down so the caller boots a clean container (otherwise every
     * project would show the previously opened project's files/dev server).
     */
    prepareFor(applicationId: string): void {
        if (this.mounted && this.mountedFor !== applicationId) {
            this.teardown();
        }
    }

    /**
     * Mount the workspace (one mount call for the whole tree), then get
     * node_modules in place from the fastest available source. Pending file ops
     * that arrived while booting are replayed afterwards.
     */
    async mount(files: Record<string, string>, forApplicationId?: string, onLog: LogFn = () => {}): Promise<void> {
        const wc = await this.boot();
        await wc.mount(toFileSystemTree(files));

        const pkg = files['package.json'];
        if (pkg) {
            this.depsHash = await sha256Hex(pkg);
            this.depsKey = `nm:${SNAP_VERSION}:${this.depsHash}`;
            let snapshot = await snapGet(this.depsKey);
            let source = 'this browser';
            if (!snapshot) {
                snapshot = await fetchServerSnapshot(this.depsHash, onLog);
                source = 'the platform';
                // mount() TRANSFERS the buffer to the container worker (detached
                // afterwards), so hand IndexedDB its own copy for the next boot.
                if (snapshot) void snapSet(this.depsKey, snapshot.slice());
            }
            if (snapshot) {
                try {
                    const t0 = performance.now();
                    await wc.fs.mkdir('node_modules', { recursive: true }).catch(() => undefined);
                    await wc.mount(snapshot, { mountPoint: 'node_modules' });
                    onLog(`Dependencies restored from ${source} in ${((performance.now() - t0) / 1000).toFixed(1)}s — skipping npm install\n`);
                    this.depsFromCache = true;
                } catch {
                    this.depsFromCache = false; // fall back to npm install
                }
            }
        }
        this.mounted = true;
        this.mountedFor = forApplicationId ?? null;

        // Replay edits the agent made while we were booting.
        const ops = this.pending;
        this.pending = [];
        for (const op of ops) {
            try {
                if (op.kind === 'write') await this.writeFile(op.path, op.contents);
                else if (op.kind === 'delete') await this.deleteFile(op.path);
                else await this.renameFile(op.from, op.to);
            } catch {
                /* a stale op on a path the agent since removed — ignore */
            }
        }
    }

    /**
     * Run the dev server (after `npm install` when no snapshot was mounted).
     * Resolves with the preview URL once the dev server is listening.
     * Idempotent: repeated calls (e.g. React StrictMode's double-mount) return
     * the same in-flight/resolved URL instead of re-installing.
     */
    async start(onLog: LogFn = () => {}): Promise<string> {
        if (this.previewUrl) return this.previewUrl;
        if (this.starting) return this.starting;

        this.starting = (async () => {
            const wc = await this.boot();

            const install = async () => {
                onLog('$ npm install\n');
                const proc = await wc.spawn('npm', ['install']);
                proc.output.pipeTo(new WritableStream({ write: (d) => onLog(d) }));
                const exit = await proc.exit;
                if (exit !== 0) throw new Error(`npm install failed (exit ${exit})`);
            };

            // A mounted snapshot keeps the .bin symlinks but not their exec bit.
            const fixPermissions = async () => {
                const proc = await wc.spawn('sh', ['-c', 'chmod +x node_modules/.bin/* 2>/dev/null; true']);
                await proc.exit;
            };

            // Start the dev server; FAIL (don't hang) if it exits before ready —
            // e.g. a broken dependency cache making the launcher unrunnable.
            const runDev = async (): Promise<string> => {
                onLog('$ npm run dev\n');
                const dev = await wc.spawn('npm', ['run', 'dev']);
                dev.output.pipeTo(new WritableStream({ write: (d) => onLog(d) }));
                return new Promise<string>((resolve, reject) => {
                    let settled = false;
                    wc.on('server-ready', (_port, u) => {
                        if (settled) return;
                        settled = true;
                        onLog(`\nPreview ready at ${u}\n`);
                        resolve(u);
                    });
                    void dev.exit.then((code) => {
                        if (settled) return;
                        settled = true;
                        reject(new Error(`dev server exited (code ${code}) before becoming ready`));
                    });
                });
            };

            // Sanity-check a mounted snapshot before trusting it (the .bin
            // launcher must exist or npm run dev cannot find the CLI).
            if (this.depsFromCache) {
                const binOk = await wc.fs
                    .readdir('node_modules/.bin')
                    .then((entries) => entries.includes('astro') || entries.includes('vite'))
                    .catch(() => false);
                if (!binOk) {
                    onLog('[cache] Cached dependencies incomplete — reinstalling…\n');
                    await this.discardDepsCache(wc);
                }
            }

            let url: string;
            if (this.depsFromCache) {
                try {
                    await fixPermissions();
                    url = await runDev();
                } catch (e: any) {
                    // Self-heal: poisoned cache -> discard, full install, retry once.
                    onLog(`\n[cache] Cached dependencies broken (${e?.message}) — reinstalling…\n`);
                    await this.discardDepsCache(wc);
                    await install();
                    url = await runDev();
                    void this.exportForNextBoot(wc);
                }
            } else {
                await install();
                url = await runDev();
                void this.exportForNextBoot(wc);
            }
            this.previewUrl = url;
            return url;
        })().catch((e) => {
            this.starting = null; // allow a retry after failures
            throw e;
        });

        return this.starting;
    }

    /** Freshly installed deps: snapshot node_modules so the NEXT boot skips npm install. */
    private async exportForNextBoot(wc: WebContainer): Promise<void> {
        if (!this.depsKey) return;
        try {
            const data = (await wc.export('node_modules', { format: 'binary' })) as Uint8Array;
            await snapSet(this.depsKey, data);
        } catch {
            /* export unsupported / quota — next boot just installs again */
        }
    }

    /** Drop the cached snapshot (memory + IndexedDB) and clear node_modules. */
    private async discardDepsCache(wc: WebContainer): Promise<void> {
        this.depsFromCache = false;
        if (this.depsKey) await snapDelete(this.depsKey);
        await wc.fs.rm('node_modules', { recursive: true, force: true }).catch(() => undefined);
    }

    /**
     * Write a file into the virtual FS (creates parent dirs). Triggers HMR.
     * Before the workspace is mounted the write is queued and replayed after
     * mount, so agent output produced during boot is never lost or overwritten.
     */
    async writeFile(path: string, contents: string): Promise<void> {
        if (!this.mounted) {
            this.pending.push({ kind: 'write', path, contents });
            return;
        }
        const wc = await this.boot();
        const dir = path.split('/').slice(0, -1).join('/');
        if (dir) await wc.fs.mkdir(dir, { recursive: true }).catch(() => undefined);
        await wc.fs.writeFile(path, contents);
    }

    /** Delete a file (or directory) from the virtual FS. */
    async deleteFile(path: string): Promise<void> {
        if (!this.mounted) {
            this.pending.push({ kind: 'delete', path });
            return;
        }
        const wc = await this.boot();
        await wc.fs.rm(path, { recursive: true, force: true });
    }

    /** Rename/move a file by copying its contents to the new path and removing the old. */
    async renameFile(oldPath: string, newPath: string): Promise<void> {
        if (!this.mounted) {
            this.pending.push({ kind: 'rename', from: oldPath, to: newPath });
            return;
        }
        const contents = await this.readFile(oldPath);
        await this.writeFile(newPath, contents);
        await this.deleteFile(oldPath);
    }

    /** Read a file's current contents (for seeding the editor). */
    async readFile(path: string): Promise<string> {
        const wc = await this.boot();
        return wc.fs.readFile(path, 'utf-8');
    }

    /**
     * Recursively list project file paths (relative, no leading slash), skipping
     * generated/vendor dirs. Used to populate the file-tree sidebar.
     */
    async listFiles(root = '/'): Promise<string[]> {
        const wc = await this.boot();
        const IGNORE = new Set(['node_modules', '.git', 'dist', '.vite', '.cache', '.astro']);
        const out: string[] = [];

        const walk = async (dir: string) => {
            const entries = await wc.fs.readdir(dir, { withFileTypes: true });
            for (const entry of entries) {
                if (IGNORE.has(entry.name)) continue;
                const full = dir === '/' ? `/${entry.name}` : `${dir}/${entry.name}`;
                if (entry.isDirectory()) await walk(full);
                else out.push(full.replace(/^\//, ''));
            }
        };

        await walk(root);
        return out.sort();
    }

    /** Tear down so a fresh session can boot. Call explicitly, not on every unmount. */
    teardown(): void {
        this.instance?.teardown();
        this.instance = null;
        this.booting = null;
        this.starting = null;
        this.previewUrl = null;
        this.mounted = false;
        this.mountedFor = null;
        this.depsFromCache = false;
        this.depsKey = null;
        this.depsHash = null;
        this.pending = [];
    }
}

// Module singleton — one WebContainer per page/tab.
export const webContainerService = new WebContainerService();
